/**
 * Progress Utilities - Calcoli 1RM e Suggerimenti Peso
 *
 * Formule basate su standard NSCA e letteratura scientifica
 */

// Tabella percentuali NSCA standard
export const PERCENTAGE_TABLE = {
  1: 100,
  2: 95,
  3: 93,
  4: 90,
  5: 87,
  6: 85,
  7: 83,
  8: 80,
  9: 77,
  10: 75,
  12: 70,
  15: 65
};

/**
 * Calcola 1RM usando la formula Epley
 * Epley: 1RM = weight × (1 + reps/30)
 */
export const calculateEpley = (weight, reps) => {
  if (reps === 1) return weight;
  if (reps <= 0 || weight <= 0) return null;
  return weight * (1 + reps / 30);
};

/**
 * Calcola 1RM usando la formula Brzycki
 * Brzycki: 1RM = weight × (36 / (37 - reps))
 */
export const calculateBrzycki = (weight, reps) => {
  if (reps === 1) return weight;
  if (reps <= 0 || weight <= 0 || reps >= 37) return null;
  return weight * (36 / (37 - reps));
};

/**
 * Calcola 1RM stimato come media di Epley e Brzycki
 * Più accurato per range 1-12 reps
 *
 * @param {number} weight - Peso utilizzato
 * @param {number} reps - Ripetizioni effettuate
 * @returns {number|null} - 1RM stimato o null se non calcolabile
 */
export const calculate1RM = (weight, reps) => {
  if (reps <= 0 || weight <= 0) return null;
  if (reps === 1) return weight;
  if (reps > 12) return null; // Troppo impreciso oltre 12 reps

  const epley = calculateEpley(weight, reps);
  const brzycki = calculateBrzycki(weight, reps);

  if (epley === null || brzycki === null) return null;

  return (epley + brzycki) / 2;
};

/**
 * Dato un 1RM, calcola il peso per un target di ripetizioni
 *
 * @param {number} oneRM - 1RM stimato
 * @param {number} targetReps - Ripetizioni target
 * @returns {number} - Peso suggerito
 */
export const getWeightForReps = (oneRM, targetReps) => {
  if (!oneRM || oneRM <= 0) return null;

  // Trova la percentuale più vicina
  const percentage = PERCENTAGE_TABLE[targetReps] ||
    (targetReps > 15 ? 60 : PERCENTAGE_TABLE[Math.min(targetReps, 15)]);

  return oneRM * (percentage / 100);
};

/**
 * Calcola range di peso per un target di ripetizioni
 * Restituisce un range ±2.5% per flessibilità
 *
 * @param {number} oneRM - 1RM stimato
 * @param {number} targetReps - Ripetizioni target
 * @returns {object} - { min, max, suggested, percentage }
 */
export const getWeightRangeForReps = (oneRM, targetReps) => {
  const suggested = getWeightForReps(oneRM, targetReps);
  if (!suggested) return null;

  const percentage = PERCENTAGE_TABLE[targetReps] || 75;

  return {
    min: Math.round(suggested * 0.975 * 2) / 2, // Arrotonda a 0.5kg
    max: Math.round(suggested * 1.025 * 2) / 2,
    suggested: Math.round(suggested * 2) / 2,
    percentage
  };
};

/**
 * Analizza la stabilità delle ultime sessioni
 *
 * @param {Array} sessions - Array di sessioni { weight, reps }
 * @returns {object} - { isStable, avgWeight, avgReps, variation }
 */
export const analyzeSessionStability = (sessions) => {
  if (!sessions || sessions.length < 2) {
    return { isStable: false, avgWeight: null, avgReps: null, variation: null };
  }

  // Prendi solo le ultime 3 sessioni
  const recentSessions = sessions.slice(0, 3);

  // Calcola medie
  const avgWeight = recentSessions.reduce((sum, s) => sum + s.weight, 0) / recentSessions.length;
  const avgReps = recentSessions.reduce((sum, s) => sum + s.reps, 0) / recentSessions.length;

  // Calcola variazione percentuale del peso
  const weights = recentSessions.map(s => s.weight);
  const maxWeight = Math.max(...weights);
  const minWeight = Math.min(...weights);
  const variation = maxWeight > 0 ? ((maxWeight - minWeight) / avgWeight) * 100 : 0;

  // Stabile se variazione < 5%
  const isStable = variation < 5;

  return { isStable, avgWeight, avgReps, variation: Math.round(variation * 10) / 10 };
};

/**
 * Genera suggerimento peso intelligente
 *
 * @param {Array} history - Storico sessioni (più recente prima)
 * @param {number} targetReps - Ripetizioni target (opzionale)
 * @returns {object} - Suggerimento completo
 */
export const generateWeightSuggestion = (history, targetReps = null) => {
  if (!history || history.length === 0) {
    return {
      hasSuggestion: false,
      message: 'Nessun dato storico disponibile'
    };
  }

  // Prepara dati sessioni (peso max e reps medie per sessione)
  const sessions = history.map(session => {
    const weight = session.maxWeight || session.weight;
    const reps = session.avgReps || session.reps;
    return { weight, reps, date: session.date };
  }).filter(s => s.weight > 0 && s.reps > 0);

  if (sessions.length === 0) {
    return {
      hasSuggestion: false,
      message: 'Dati insufficienti'
    };
  }

  // Calcola 1RM dalle sessioni recenti
  const oneRMs = sessions.slice(0, 3).map(s => calculate1RM(s.weight, s.reps)).filter(Boolean);

  if (oneRMs.length === 0) {
    return {
      hasSuggestion: false,
      message: 'Impossibile calcolare 1RM'
    };
  }

  const avg1RM = oneRMs.reduce((sum, rm) => sum + rm, 0) / oneRMs.length;

  // Analizza stabilità
  const stability = analyzeSessionStability(sessions);

  // Determina target reps (usa quello passato o la media recente)
  const effectiveTargetReps = targetReps || Math.round(stability.avgReps) || 8;

  // Calcola range peso
  const weightRange = getWeightRangeForReps(avg1RM, effectiveTargetReps);

  if (!weightRange) {
    return {
      hasSuggestion: false,
      message: 'Impossibile calcolare suggerimento'
    };
  }

  // Se stabile, suggerisci microprogressione (increment dinamico)
  let suggestion = weightRange.suggested;
  let progressionNote = '';

  if (stability.isStable && sessions.length >= 3) {
    const lastWeight = sessions[0].weight;
    const incrementStep = history[0]?.incrementStep || 2.5; // ← DINAMICO
    const progressionWeight = lastWeight + incrementStep;

    // Suggerisci progressione solo se ragionevole (non oltre il range calcolato)
    if (progressionWeight <= weightRange.max + (incrementStep * 2)) {
      suggestion = progressionWeight;
      progressionNote = `+${incrementStep}kg`;
    }
  }

  return {
    hasSuggestion: true,
    estimated1RM: Math.round(avg1RM * 10) / 10,
    targetReps: effectiveTargetReps,
    suggestedWeight: Math.round(suggestion * 2) / 2, // Arrotonda a 0.5kg
    incrementStep: history[0]?.incrementStep || 2.5, // ← NUOVO: Pass to caller
    weightRange: {
      min: weightRange.min,
      max: weightRange.max
    },
    percentage: weightRange.percentage,
    isStable: stability.isStable,
    progressionNote,
    lastWeight: sessions[0]?.weight,
    lastReps: sessions[0]?.reps,
    sessionsAnalyzed: sessions.length
  };
};

/**
 * Formatta il suggerimento per la UI
 *
 * @param {object} suggestion - Output di generateWeightSuggestion
 * @returns {string} - Testo formattato
 */
export const formatSuggestion = (suggestion) => {
  if (!suggestion.hasSuggestion) {
    return null;
  }

  const { suggestedWeight, weightRange, progressionNote } = suggestion;

  if (progressionNote) {
    return `${suggestedWeight}kg (${progressionNote})`;
  }

  if (weightRange.min !== weightRange.max) {
    return `${weightRange.min}-${weightRange.max}kg`;
  }

  return `${suggestedWeight}kg`;
};

/**
 * Calcola tutte le percentuali di lavoro da un 1RM
 *
 * @param {number} oneRM - 1RM stimato
 * @returns {Array} - Array di { reps, percentage, weight }
 */
export const calculateAllPercentages = (oneRM) => {
  if (!oneRM || oneRM <= 0) return [];

  return Object.entries(PERCENTAGE_TABLE).map(([reps, percentage]) => ({
    reps: parseInt(reps),
    percentage,
    weight: Math.round(oneRM * (percentage / 100) * 2) / 2
  }));
};
